import { findCountryCode } from "@redotech/locale/countries";

export enum ShippingInsurance {
  DISABLED = "disabled",
  ENABLED = "enabled",
}

export enum UpsCapitalCommodityProgram {
  DOMESTIC_EXPORT = "domestic_export",
  INTERNATIONAL = "international",
}

export enum UpsCapitalCommodityPayer {
  MERCHANT = "merchant",
  CHECKOUT = "checkout",
}

export enum UpsCapitalCommodityCarrier {
  ANY = "any",
  EXPRESS = "express",
  POSTAL = "postal",
}

export type UpsCapitalCommodity =
  | {
      code: string;
      program: UpsCapitalCommodityProgram;
      payer: UpsCapitalCommodityPayer;
      carrier: UpsCapitalCommodityCarrier;
      /** 1 through 4 by loss ratio, or FPA (named perils only). */
      tier: "1" | "2" | "3" | "4" | "FPA";
      /** Premium as a percentage of insured value. */
      ratePercent: number;
      sandboxOnly?: false;
      /** Floor on the premium; the matrix rows have none. */
      minimumPremium: number;
    }
  | {
      /** Only the UAT sandbox account rates this code; it is not in the matrix. */
      code: string;
      ratePercent: number;
      sandboxOnly: true;
      minimumPremium: number;
    };

const { DOMESTIC_EXPORT, INTERNATIONAL } = UpsCapitalCommodityProgram;
const { MERCHANT, CHECKOUT } = UpsCapitalCommodityPayer;
const { ANY, EXPRESS, POSTAL } = UpsCapitalCommodityCarrier;

function program(
  programKind: UpsCapitalCommodityProgram,
  payer: UpsCapitalCommodityPayer,
  carrier: UpsCapitalCommodityCarrier,
  rows: ["1" | "2" | "3" | "4" | "FPA", string, number][],
): UpsCapitalCommodity[] {
  return rows.map(([tier, code, ratePercent]) => ({
    minimumPremium: 0,
    code,
    program: programKind,
    payer,
    carrier,
    tier,
    ratePercent,
  }));
}

/** The Redo Tech commodity and pricing matrix from UPS Capital (October 2026), plus the sandbox code. */
export const UPS_CAPITAL_COMMODITIES: readonly UpsCapitalCommodity[] = [
  { code: "400", ratePercent: 1.6, minimumPremium: 1.6, sandboxOnly: true },
  ...program(DOMESTIC_EXPORT, MERCHANT, ANY, [
    ["1", "500", 0.75],
    ["2", "501", 1.0],
    ["3", "502", 1.95],
    ["4", "503", 2.5],
    ["FPA", "504", 2.5],
  ]),
  ...program(DOMESTIC_EXPORT, CHECKOUT, ANY, [
    ["1", "505", 0.9],
    ["2", "506", 1.15],
    ["3", "507", 2.1],
    ["4", "508", 2.65],
    ["FPA", "509", 2.65],
  ]),
  ...program(INTERNATIONAL, MERCHANT, ANY, [
    ["1", "510", 0.75],
    ["2", "511", 1.0],
    ["3", "512", 1.95],
    ["4", "513", 2.5],
    ["FPA", "514", 2.5],
  ]),
  ...program(INTERNATIONAL, CHECKOUT, EXPRESS, [
    ["1", "515", 0.8],
    ["2", "516", 1.05],
    ["3", "517", 2.0],
    ["4", "518", 2.65],
    ["FPA", "519", 2.65],
  ]),
  ...program(INTERNATIONAL, CHECKOUT, POSTAL, [
    ["1", "520", 0.9],
    ["2", "600", 1.15],
    ["3", "601", 2.1],
    ["4", "602", 2.65],
    ["FPA", "603", 2.65],
  ]),
];

/** International, shopper-paid, express carrier, tier 1: every merchant's starting code, saved explicitly so active PP+ teams are visible. */
export const DEFAULT_UPS_CAPITAL_COMMODITY_CODE = "515";

/** The team's row, or the program default when the code is unset or unknown. */
export function resolveUpsCapitalCommodity(
  code: string | null | undefined,
): UpsCapitalCommodity {
  const commodity =
    upsCapitalCommodityByCode(code) ??
    upsCapitalCommodityByCode(DEFAULT_UPS_CAPITAL_COMMODITY_CODE);
  if (!commodity) {
    throw new Error("UPS Capital commodity matrix lacks the default code");
  }
  return commodity;
}

export function upsCapitalCommodityByCode(
  code: string | null | undefined,
): UpsCapitalCommodity | undefined {
  return UPS_CAPITAL_COMMODITIES.find((commodity) => commodity.code === code);
}

export function upsCapitalCommodityLabel(
  commodity: UpsCapitalCommodity,
): string {
  if (commodity.sandboxOnly) {
    return `${commodity.code} · Staging only (UAT sandbox account) · ${commodity.ratePercent.toFixed(2)}%`;
  }
  const programLabel =
    commodity.program === UpsCapitalCommodityProgram.INTERNATIONAL
      ? "International"
      : "Domestic-Export";
  const payerLabel =
    commodity.payer === UpsCapitalCommodityPayer.MERCHANT
      ? "Merchant paid"
      : "Checkout";
  const carrierLabel = {
    [UpsCapitalCommodityCarrier.ANY]: null,
    [UpsCapitalCommodityCarrier.EXPRESS]: "Express carrier",
    [UpsCapitalCommodityCarrier.POSTAL]: "Postal",
  }[commodity.carrier];
  const tierLabel = commodity.tier === "FPA" ? "FPA" : `Tier ${commodity.tier}`;
  return [
    commodity.code,
    programLabel,
    payerLabel,
    carrierLabel,
    tierLabel,
    `${commodity.ratePercent.toFixed(2)}%`,
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

/**
 * Accepts an ISO alpha-2 code or a country name as stored on addresses. The
 * recording path and the storefront autocheck must agree on this, or a cart
 * can show one insurer while the shipment is recorded with the other.
 */
export function toCountryCode(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return findCountryCode(trimmed) ?? null;
}
